import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';

const textMuted = '#364153';
const textRange = '#6a7282';
const borderGray = '#d1d5dc';
const navDark = '#1e2a39';
const topBorder = '#e5e7eb';

const DEFAULT_LIMIT_OPTIONS = [8, 10, 20, 50] as const;

function visiblePageNumbers(current: number, totalPages: number, maxVisible = 4): number[] {
  if (totalPages <= 0) return [];
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  let start = Math.max(1, current - Math.floor(maxVisible / 2));
  const end = Math.min(totalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export type HomePaginationBarProps = {
  page: number;
  totalCount: number;
  limit: number;
  limitOptions?: readonly number[];
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
};

export function HomePaginationBar({
  page,
  totalCount,
  limit,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  onPageChange,
  onLimitChange,
}: HomePaginationBarProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / limit) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = totalCount === 0 ? 0 : (safePage - 1) * limit + 1;
  const end = Math.min(safePage * limit, totalCount);

  const pages = visiblePageNumbers(safePage, totalPages, 4);
  const canPrev = safePage > 1;
  const canNext = safePage < totalPages;

  return (
    <Box
      sx={{
        borderTop: `0.8px solid ${topBorder}`,
        pt: '14px',
        mt: '10px',
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 2, sm: 2 },
        flexWrap: 'wrap',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: { xs: 1, md: '35px' },
          flex: 1,
          minWidth: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 400, color: textMuted, lineHeight: '20px' }}>
            Show
          </Typography>
          <Select
            value={limit}
            onChange={e => onLimitChange(Number(e.target.value))}
            size="small"
            variant="outlined"
            sx={{
              minWidth: 50,
              height: 33,
              borderRadius: '5px',
              fontSize: 12,
              color: navDark,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: borderGray,
                borderWidth: '0.8px',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: borderGray },
              '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center' },
            }}
          >
            {limitOptions.map(n => (
              <MenuItem key={n} value={n} dense sx={{ fontSize: 12 }}>
                {n}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontSize: 12, fontWeight: 400, color: textMuted, lineHeight: '20px' }}>
            entries
          </Typography>
        </Box>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 400,
            color: textRange,
            lineHeight: '20px',
            whiteSpace: 'nowrap',
          }}
        >
          Showing {start} to {end} of {totalCount} entries
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '6.7px',
          flexShrink: 0,
          alignSelf: { xs: 'flex-end', sm: 'auto' },
        }}
      >
        <IconButton
          type="button"
          size="small"
          disabled={!canPrev}
          onClick={() => onPageChange(safePage - 1)}
          aria-label="Previous page"
          sx={{
            width: 28.33,
            height: 28.33,
            borderRadius: '6.75px',
            color: canPrev ? navDark : 'rgba(30, 42, 57, 0.35)',
            opacity: canPrev ? 1 : 0.5,
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: '3.4px' }}>
          {pages.map(p => {
            const active = p === safePage;
            return (
              <Box
                key={p}
                component="button"
                type="button"
                onClick={() => onPageChange(p)}
                sx={{
                  minWidth: 33.7,
                  height: 30.35,
                  px: 0.5,
                  borderRadius: '8.43px',
                  border: active ? 'none' : `0.675px solid ${borderGray}`,
                  bgcolor: active ? navDark : 'transparent',
                  color: active ? '#fff' : navDark,
                  fontSize: 11,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  lineHeight: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {p}
              </Box>
            );
          })}
        </Box>

        <IconButton
          type="button"
          size="small"
          disabled={!canNext}
          onClick={() => onPageChange(safePage + 1)}
          aria-label="Next page"
          sx={{
            width: 28.33,
            height: 28.33,
            borderRadius: '6.75px',
            color: canNext ? navDark : 'rgba(30, 42, 57, 0.35)',
            opacity: canNext ? 1 : 0.5,
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
